import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import type { ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { Loader2, Eye, Plus } from "lucide-react";
import { toast } from "sonner";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { QuickViewDialog } from "./QuickViewDialog";
import { Price } from "./Price";
import { StarRating } from "./StarRating";
import { hasActiveNewBadge } from "@/lib/productBadges";
import { getProductRatings } from "@/lib/reviews.functions";

const ratingsQuery = {
  queryKey: ["product-ratings"] as const,
  queryFn: () => getProductRatings(),
  staleTime: 5 * 60 * 1000,
};

export function ProductCard({ product }: { product: ShopifyProduct }) {
  const addItem = useCartStore(s => s.addItem);
  const isLoading = useCartStore(s => s.isLoading);
  const { data: settings } = useSiteSettings();
  const fetchRatings = useServerFn(getProductRatings);
  const { data: ratingsData } = useQuery({ ...ratingsQuery, queryFn: fetchRatings });
  const [hovered, setHovered] = useState(false);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const { node } = product;
  const rating = ratingsData?.ratings?.[node.handle];

  const images = node.images.edges;
  const primaryImage = images[0]?.node;
  const secondaryImage = images[1]?.node;
  const price = node.priceRange.minVariantPrice;
  const variant = node.variants.edges[0]?.node;

  // Color count (from "Color" option)
  const colorOption = node.options?.find(o => o.name.toLowerCase() === "color");
  const colorCount = colorOption?.values.length ?? 0;

  // Compare-at price for sale display
  const compareAt = variant?.compareAtPrice;
  const priceNum = parseFloat(price.amount);
  const compareNum = compareAt ? parseFloat(compareAt.amount) : 0;
  const onSale = Boolean(compareAt && compareNum > priceNum);

  // Sold-out detection: every variant unavailable
  const allSoldOut = node.variants.edges.length > 0 && node.variants.edges.every(v => !v.node.availableForSale);

  const searchableProductText = [
    node.title,
    node.handle,
    node.productType,
    node.description,
    ...(node.tags ?? []),
  ].join(" ").toLowerCase();
  const shirtLanguage = /\b(t[-\s]?shirt|tee|tees|shirt|shirts|top|tops|apparel|garment[-\s]?dyed|unisex)\b/.test(searchableProductText);
  const apparelSizes = new Set(["xs", "s", "m", "l", "xl", "2xl", "3xl", "4xl", "xxl", "xxxl"]);
  const hasApparelSizing = node.options?.some(option =>
    option.name.toLowerCase() === "size" &&
    option.values.some(value => apparelSizes.has(value.toLowerCase().replace(/\s+/g, "")))
  );
  const isShirt = shirtLanguage || hasApparelSizing;
  const isNew = hasActiveNewBadge(node.handle);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!variant) return;
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
    toast.success(`${node.title} added to cart`, { position: "bottom-right" });
  };

  const handleQuickView = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setQuickViewOpen(true);
  };

  return (
    <>
      <Link
        to="/products/$handle"
        params={{ handle: node.handle }}
        className="group block"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div>
          {/* Image */}
          <div className="relative aspect-square overflow-hidden rounded-sm bg-[#FDFAF3] border-[0.5px] border-[#D4CCBC]">

            {primaryImage ? (
              <>
                <img
                  src={primaryImage.url}
                  alt={primaryImage.altText || node.title}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${hovered && secondaryImage ? "opacity-0" : "opacity-100"}`}
                  loading="lazy"
                />
                {secondaryImage && (
                  <img
                    src={secondaryImage.url}
                    alt={secondaryImage.altText || node.title}
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${hovered ? "opacity-100 scale-105" : "opacity-0"}`}
                    loading="lazy"
                  />
                )}
              </>
            ) : (
              <div className="flex h-full items-center justify-center bg-[#FDFAF3]">
                <span className="font-ui text-sm text-[#8B6F47]">No image</span>
              </div>
            )}

            {/* Product status badges */}
            <div className="absolute right-2 top-2 z-10 flex flex-col items-end gap-1 sm:right-3 sm:top-3 sm:gap-2">
              {allSoldOut ? (
                <span className="rounded-md bg-faded-barn px-1.5 py-0.5 font-ui text-[8px] font-extrabold uppercase leading-tight tracking-normal text-[#F5EFE2] sm:px-2.5 sm:py-1 sm:text-[11px]">
                  Sold Out
                </span>
              ) : (
                <>
                  {settings?.promo_buy2get1_enabled && !isShirt && (
                    <span className="rounded-md bg-sage-brush px-1.5 py-0.5 font-ui text-[8px] font-extrabold uppercase leading-tight tracking-normal text-[#F5EFE2] sm:px-2.5 sm:py-1 sm:text-[11px]">
                      B2G1
                    </span>
                  )}
                  {isNew && (
                    <span className="rounded-md bg-creek-water px-1.5 py-0.5 font-ui text-[8px] font-extrabold uppercase leading-tight tracking-normal text-[#F5EFE2] sm:px-2.5 sm:py-1 sm:text-[11px]">
                      New
                    </span>
                  )}
                  {onSale && (
                    <span className="rounded-md bg-blaze px-1.5 py-0.5 font-ui text-[8px] font-extrabold uppercase leading-tight tracking-normal text-[#F5EFE2] sm:px-2.5 sm:py-1 sm:text-[11px]">
                      Sale
                    </span>
                  )}
                </>
              )}
            </div>

            {/* Quick View — slides in on hover (desktop) */}
            <button
              onClick={handleQuickView}
              aria-label={`Quick view ${node.title}`}
              className="absolute left-3 top-3 z-10 hidden h-9 w-9 items-center justify-center rounded-full bg-[#FDFAF3] text-[#2A1510] opacity-0 shadow-md transition-all duration-200 hover:bg-[#FA5021] hover:text-[#F5EFE2] group-hover:opacity-100 md:flex"
            >
              <Eye className="h-4 w-4" />
            </button>

            {/* Mobile + add button */}
            {!allSoldOut && variant?.availableForSale && (
              <button
                onClick={handleAddToCart}
                disabled={isLoading}
                aria-label={`Add ${node.title} to cart`}
                className="absolute right-1.5 bottom-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-[#FDFAF3] text-[#2A1510] shadow-sm transition-all duration-150 active:scale-90 md:hidden"
              >
                {isLoading ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <Plus className="h-2.5 w-2.5" strokeWidth={3} />
                )}
              </button>
            )}

            {/* Desktop hover "Add to Bag" bar */}
            {!allSoldOut && variant?.availableForSale && (
              <button
                onClick={handleAddToCart}
                disabled={isLoading}
                aria-label={`Add ${node.title} to cart`}
                className="absolute inset-x-0 bottom-0 z-10 hidden translate-y-2 items-center justify-center bg-[#FDFAF3]/95 px-4 py-3 font-ui text-xs font-semibold uppercase tracking-[3px] text-[#2A1510] opacity-0 backdrop-blur-sm transition-all duration-200 hover:bg-[#FA5021] hover:text-[#F5EFE2] group-hover:translate-y-0 group-hover:opacity-100 disabled:opacity-50 md:flex"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to Bag"}
              </button>
            )}
          </div>

          {/* Info */}
          <div className="p-4 text-center">
            <h3 className="font-heading text-lg font-bold uppercase text-[#2A1510]">{node.title}</h3>

            <div className="mt-1 flex items-baseline justify-center gap-2 font-body text-xl font-bold">
              {onSale && (
                <Price amount={compareNum} className="text-[#8B6F47] line-through" />
              )}
              <Price amount={priceNum} className={onSale ? "text-[#FA5021]" : "text-[#8B6F47]"} />
            </div>

            {rating && rating.count > 0 && (
              <div className="mt-1.5 flex items-center justify-center gap-1.5">
                <StarRating rating={rating.average} size={12} />
                <span className="font-ui text-[10px] uppercase tracking-[0.1em] text-[#8B6F47]">
                  ({rating.count})
                </span>
              </div>
            )}

            {colorCount > 0 && (
              <p className="mt-2 font-ui text-[11px] uppercase tracking-[2px] text-[#8B6F47]">
                {colorCount} {colorCount === 1 ? "color" : "colors"}
              </p>
            )}
          </div>
        </div>
      </Link>

      <QuickViewDialog product={product} open={quickViewOpen} onOpenChange={setQuickViewOpen} />
    </>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-sm bg-[#FDFAF3] border-[0.5px] border-[#D4CCBC]">
      <div className="aspect-square animate-pulse bg-[#FDFAF3]" />
      <div className="space-y-3 p-4">
        <div className="h-3 w-3/4 animate-pulse rounded bg-[#F5EFE2]" />
        <div className="h-4 w-1/3 animate-pulse rounded bg-[#F5EFE2]" />
        <div className="h-10 w-full animate-pulse rounded-sm bg-[#F5EFE2]" />
      </div>
    </div>
  );
}
